import React from 'react'
import {
    Box,
    Flex,
    Text,
    Tag,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Tooltip,
} from '@chakra-ui/react'
import { Toggle } from '../../../app/components/Button/Toggle'
import { useToggleDeviceMutation, useToggleGroupMutation } from '../api/automationsApi'
import SocketIcon from '../../../assets/icons/Socket'
import { SunOutline } from '../../../assets/icons/Brightness'
import { useDebouncedCallback } from '../../../utils/helpers'

interface CardProps {
    title: any
    id: number
    deviceId?: number
    parameter?: string
    toggle?: boolean | 'mixed'
    brightness?: number
    onBrightnessChange?: (value: number) => void
    children?: React.ReactNode
    cardIcon?: React.ReactNode
    onClick?: (e: React.MouseEvent, id: number) => void
    actions?: React.ReactNode
    activeAutomations?: number
    inactiveAutomations?: number
    chanelTopicId?: number
}

export default function Card(props: CardProps) {
    const {
        title,
        id,
        toggle,
        brightness,
        deviceId,
        parameter,
        onBrightnessChange,
        children,
        cardIcon,
        onClick,
        actions,
        activeAutomations,
        inactiveAutomations,
        chanelTopicId,
    } = props

    const [toggleDeviceById, { isLoading: isToggleDeviceLoading }] =
        useToggleDeviceMutation()
    const [toggleGroupById, { isLoading: isToggleGroupLoading }] = useToggleGroupMutation()
    const handleToggle = (value) => {
        if (deviceId) {
            toggleDeviceById({
                device_id: deviceId,
                action: value ? 'on' : 'off',
                topic_id: chanelTopicId
            })

        } else {
            toggleGroupById({
                id: id,
                body: {
                    action: value ? 'on' : 'off',
                }
            })

        }
    }

    const debouncedPostBrightness = useDebouncedCallback((value: number) => {
        if (deviceId) {
            toggleDeviceById({
                device_id: deviceId,
                action: 'brightness',
                brightness: value,
                topic_id: chanelTopicId

            })
        } else {
            toggleGroupById({
                id: id,
                body: {
                    action: 'brightness',
                    brightness: value
                }
            })
        }
    }, 300)

    const handleBrightnessChange = (value) => {
        onBrightnessChange(value)
        debouncedPostBrightness(value)
    }

    const handleClick = (e: React.MouseEvent) => {
        if (onClick) {
            onClick(e, id)
        }
    }

    return (
        <Box
            maxW="376px"
            minW="184px"
            bg="white"
            rounded="xl"
            position="relative"
            onClick={onClick ? handleClick : undefined}
            _hover={onClick ? { cursor: 'pointer', background: '#F0F0F0' } : undefined}
        >
            {actions}
            <Flex direction="column" justify="space-between" align="center" h="280px" p={4}>
                <Flex
                    direction="column"
                    align="center"
                    textAlign="center"
                    h="100px"
                    justify="center"
                >
                    <Flex bg="#F0F0F0" rounded="xl" boxSize="40px" align="center" justify="center">
                        {cardIcon ?? <SocketIcon boxSize="24px" />}
                    </Flex>

                    <Tooltip label={title} placement="top" hasArrow>
                        <Text mt={3} fontWeight="bold" maxW="150px" isTruncated>
                            {title}
                        </Text>
                    </Tooltip>

                    <Box h="24px" mt={2}>
                        {(activeAutomations > 0 || inactiveAutomations > 0) ? (
                            <Flex gap={2}>
                                {activeAutomations > 0 && (
                                    <Tag colorScheme="green" fontSize="xs">
                                        Active: {activeAutomations}
                                    </Tag>
                                )}
                                {inactiveAutomations > 0 && (
                                    <Tag colorScheme="gray" fontSize="xs">
                                        Inactive: {inactiveAutomations}
                                    </Tag>
                                )}
                            </Flex>
                        ) : (
                            <Box visibility="hidden">placeholder</Box>
                        )}
                    </Box>
                </Flex>
                {typeof brightness === 'number' && onBrightnessChange && (
                    <Box w="full" px={2}>
                        <Box
                            position="relative"
                            h="52px"
                            borderRadius="8px"
                            overflow="hidden"
                            bg="#E0E0E0"
                        >
                            <Box
                                position="absolute"
                                left={0}
                                top={0}
                                bottom={0}
                                width={`${brightness}%`}
                                bgGradient="linear(to-r, #1daafc, #1683ef)"
                                transition="width 0.2s ease"
                            />
                            <Flex
                                position="absolute"
                                top={0}
                                bottom={0}
                                left={0}
                                right={0}
                                align="center"
                                justify="center"
                                zIndex={1}
                                pointerEvents="none"
                            >
                                <Text fontSize="sm" color="white" fontWeight="semibold">
                                    <SunOutline /> {brightness}%
                                </Text>
                            </Flex>
                            <Slider
                                value={brightness}
                                onChange={handleBrightnessChange}
                                min={0}
                                max={100}
                                step={1}
                                w="full"
                                h="full"
                                opacity={0}
                                position="absolute"
                                top={0}
                                left={0}
                                zIndex={2}
                            >
                                <SliderTrack>
                                    <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Box>
                    </Box>
                )}

                <Box h="40px" display="flex" alignItems="center" justifyContent="center">
                    {toggle !== undefined && (
                        <Toggle
                            handleClick={handleToggle}
                            isActive={toggle as boolean}
                            isLoading={isToggleDeviceLoading || isToggleGroupLoading}
                            isMixed={toggle === 'mixed'}
                        />
                    )}
                </Box>
            </Flex>
        </Box>
    )
}
